import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type * as React from "react";
import { cn } from "../lib/cn";

/**
 * Skeleton — shimmer placeholder for content that's loading.
 *
 * Apple HIG ``progressive-loading`` + Material ``content-jumping``: any
 * async surface taking > 300ms shows a skeleton matching the final
 * layout (size + position) instead of a blocking spinner. Reserves
 * space so the layout doesn't jump when real data arrives, avoiding
 * CLS spikes and the "el-jank" perception of slow apps.
 *
 * Single source of truth for skeleton chrome: cream-deep tint, hairline
 * radius rhythm, `prefers-reduced-motion` aware (pulse animation is
 * disabled to respect the system setting).
 */

export type SkeletonShape = "rect" | "text" | "circle" | "pill";

const SHAPE_CLASS: Record<SkeletonShape, string> = {
  rect: "rounded-md",
  text: "rounded-sm",
  circle: "rounded-full",
  pill: "rounded-full",
};

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual silhouette. ``text`` = short tracking, ``rect`` = card body,
   *  ``circle`` = avatar, ``pill`` = chip / badge placeholder. */
  readonly shape?: SkeletonShape;
  /** Width in pixels OR Tailwind class via ``className`` (default: full). */
  readonly width?: number | string;
  /** Height in pixels OR Tailwind class via ``className`` (default: 12px). */
  readonly height?: number | string;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  shape = "rect",
  width,
  height,
  className,
  style,
  ...rest
}) => {
  const inlineStyle: React.CSSProperties = {
    ...(width !== undefined
      ? { width: typeof width === "number" ? `${width}px` : width }
      : null),
    ...(height !== undefined
      ? { height: typeof height === "number" ? `${height}px` : height }
      : null),
    ...style,
  };
  return (
    <div
      aria-hidden
      className={cn(
        "block animate-pulse bg-cream-deep/60 motion-reduce:animate-none",
        SHAPE_CLASS[shape],
        className,
      )}
      style={inlineStyle}
      {...rest}
    />
  );
};
Skeleton.displayName = "Skeleton";

/**
 * SkeletonText — stack of `lines` skeleton bars sized like body text.
 * Last bar is narrower to mimic a real paragraph's ragged right edge.
 * The whole block is keyed by ``ariaLabel`` so screen readers announce
 * a single "loading" affordance instead of N empty bars.
 */
export interface SkeletonTextProps {
  readonly lines?: number;
  readonly ariaLabel?: string;
  readonly className?: string;
}

export const SkeletonText: React.FC<SkeletonTextProps> = ({
  lines = 3,
  ariaLabel,
  className,
}) => {
  const { i18n } = useLingui();
  return (
  <div
    role="status"
    aria-label={ariaLabel ?? i18n._(msg`Caricamento contenuti`)}
    aria-busy
    className={cn("flex flex-col gap-1.5", className)}
  >
    {Array.from({ length: Math.max(1, lines) }, (_, i) => (
      <Skeleton
        // Last line is shorter (60% width) so the block reads as a
        // paragraph with a ragged right edge instead of a solid block.
        key={i}
        shape="text"
        height={12}
        width={i === lines - 1 ? "60%" : "100%"}
      />
    ))}
  </div>
  );
};
SkeletonText.displayName = "SkeletonText";
